// Extraído de PeticionDetail.jsx (refactor mecánico, sin cambios funcionales)
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Pencil } from "lucide-react";
import ClienteCombobox from "@/components/ClienteCombobox";
import { ImagenUploadControl } from "@/components/ProyectoThumb";

// ============ EDITAR PETICIÓN (campos básicos) ============
export function EditPeticionDialog({ open, setOpen, peticion, clientes, users, onSaved }) {
  const [form, setForm] = useState({
    nombre: "", cliente_id: "", marca: "", comercial_id: "",
    fecha_solicitud: "", fecha_objetivo: "", descripcion: "",
    referencia_externa: "", email_origen: "", geolocalizar_fotos: true,
    imagen_url: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!peticion) return;
    setForm({
      nombre: peticion.nombre || "",
      cliente_id: peticion.cliente_id || "",
      marca: peticion.marca || "",
      comercial_id: peticion.comercial_id || "",
      fecha_solicitud: peticion.fecha_solicitud ? peticion.fecha_solicitud.slice(0, 10) : "",
      fecha_objetivo: peticion.fecha_objetivo ? peticion.fecha_objetivo.slice(0, 10) : "",
      descripcion: peticion.descripcion || "",
      referencia_externa: peticion.referencia_externa || "",
      email_origen: peticion.email_origen || "",
      geolocalizar_fotos: peticion.geolocalizar_fotos !== false,  // default true
    });
  }, [peticion, open]);

  const comerciales = users.filter((u) => (u.roles || []).includes("comercial") || (u.roles || []).includes("admin"));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.cliente_id) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.fecha_solicitud) payload.fecha_solicitud = new Date(payload.fecha_solicitud).toISOString();
      else payload.fecha_solicitud = null;
      if (payload.fecha_objetivo) payload.fecha_objetivo = new Date(payload.fecha_objetivo).toISOString();
      else payload.fecha_objetivo = null;
      ["referencia_externa", "email_origen", "marca", "comercial_id", "descripcion", "imagen_url"].forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      // Iter 115 · Comparar sólo por día (YYYY-MM-DD) para evitar falsos positivos.
      // La BD puede guardar ISO completo (importer/IA), y `new Date().toISOString()`
      // devuelve otra hora → sin `slice(0,10)` la comparación siempre daba distinto.
      const oldObjetivoIso = peticion.fecha_objetivo || null;
      const newObjetivoIso = payload.fecha_objetivo;
      const oldSlice = (oldObjetivoIso || "").slice(0, 10);
      const newSlice = (newObjetivoIso || "").slice(0, 10);
      const fechaObjetivoCambio = oldSlice !== newSlice;
      let propagarLineas = false;
      if (fechaObjetivoCambio && newObjetivoIso) {
        try {
          const { data: det } = await api.get(`/peticiones/${peticion.id}`);
          const lns = det?.lineas || [];
          const candidatas = lns.filter((l) => {
            const fp = (l.fecha_programada || "").slice(0, 10);
            // línea sin fecha explícita = no tiene fecha_programada, o coincide con la antigua objetivo
            return !fp || (oldSlice && fp === oldSlice);
          });
          if (candidatas.length > 0) {
            propagarLineas = window.confirm(
              `Has cambiado la fecha objetivo de la petición.\n\n` +
              `Hay ${candidatas.length} línea(s) sin fecha propia (o alineada con la anterior).\n\n` +
              `¿Propagar la nueva fecha (${newSlice}) a esas líneas?\n\n` +
              `• Aceptar: se actualizan sus fechas de montaje\n` +
              `• Cancelar: solo cambia la fecha objetivo (las líneas mantienen las suyas)`
            );
            payload._lineas_a_propagar = propagarLineas ? candidatas.map((l) => l.id) : [];
          }
        } catch (err) {
          // Si falla el detalle, no bloquear el guardado
        }
      }
      const lineasIdsPropagar = payload._lineas_a_propagar;
      delete payload._lineas_a_propagar;
      await api.patch(`/peticiones/${peticion.id}`, payload);
      // Propagar fecha a las líneas seleccionadas (si aplica)
      if (lineasIdsPropagar && lineasIdsPropagar.length > 0) {
        const rs = await Promise.allSettled(lineasIdsPropagar.map((lid) =>
          api.patch(`/lineas/${lid}`, { fecha_programada: newObjetivoIso })
        ));
        const fallidas = rs.filter((r) => r.status === "rejected");
        if (fallidas.length) toast.warning(`${fallidas.length} línea(s) no se reprogramaron (en ruta o cerradas): ${fallidas.map((r) => r.reason?.response?.data?.detail?.msg || "").filter(Boolean)[0] || ""}`, { duration: 8000 });
      }
      // Notificar a otras vistas (listado, dashboard…) para que recarguen
      try {
        const ev = new BroadcastChannel("dnf-entity-changes");
        ev.postMessage({ entityType: "peticion", id: peticion.id, ts: Date.now() });
        ev.close();
      } catch { /* fallback ignored */ }
      setOpen(false);
      onSaved && onSaved();
    } finally { setSaving(false); }
  };

  if (!peticion) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-4 h-4 text-amber-400" /> Editar petición
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Nombre de la campaña *</Label>
              <Input data-testid="edit-peticion-nombre" required value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                className="bg-zinc-900 border-zinc-800" />
            </div>
            <div>
              <Label>Cliente *</Label>
              <ClienteCombobox
                value={form.cliente_id}
                onChange={(id) => setForm((f) => ({ ...f, cliente_id: id }))}
                clientes={clientes}
                testId="edit-peticion-cliente"
              />
            </div>
            <div>
              <Label>Marca / Producto</Label>
              <Input value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })}
                placeholder='ej. "YSL Libre"' className="bg-zinc-900 border-zinc-800" />
            </div>
            <div>
              <Label>Comercial</Label>
              <Select value={form.comercial_id || "__none__"}
                onValueChange={(v) => setForm({ ...form, comercial_id: v === "__none__" ? "" : v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  <SelectItem value="__none__">—</SelectItem>
                  {comerciales.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Referencia externa</Label>
              <Input value={form.referencia_externa}
                onChange={(e) => setForm({ ...form, referencia_externa: e.target.value })}
                placeholder="Asunto del email, nº pedido cliente…"
                className="bg-zinc-900 border-zinc-800" />
            </div>
            <div>
              <Label>Fecha solicitud</Label>
              <Input type="date" value={form.fecha_solicitud}
                onChange={(e) => setForm({ ...form, fecha_solicitud: e.target.value })}
                className="bg-zinc-900 border-zinc-800" />
            </div>
            <div>
              <Label>Fecha objetivo (entrega)</Label>
              <Input type="date" value={form.fecha_objetivo}
                onChange={(e) => setForm({ ...form, fecha_objetivo: e.target.value })}
                className="bg-zinc-900 border-zinc-800" />
            </div>
            <div className="col-span-2">
              <Label>Email origen</Label>
              <Input value={form.email_origen}
                onChange={(e) => setForm({ ...form, email_origen: e.target.value })}
                placeholder="cliente@ejemplo.com" className="bg-zinc-900 border-zinc-800" />
            </div>
            <div className="col-span-2">
              <Label>Descripción</Label>
              <Textarea value={form.descripcion}
                onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                className="bg-zinc-900 border-zinc-800" rows={3} />
            </div>
            <div className="col-span-2">
              <ImagenUploadControl value={form.imagen_url || null} nombre={form.nombre}
                onChange={(u) => setForm({ ...form, imagen_url: u || "" })}
                folder="dnf/peticiones" label="Imagen del proyecto (miniatura en listados y paradas)" />
            </div>
            <div className="col-span-2 bg-blue-500/5 border border-blue-500/30 rounded-lg p-3 flex items-center gap-3">
              <input
                type="checkbox"
                id="geoloc-fotos"
                checked={form.geolocalizar_fotos !== false}
                onChange={(e) => setForm({ ...form, geolocalizar_fotos: e.target.checked })}
                data-testid="edit-peticion-geoloc"
                className="accent-blue-500 w-4 h-4"
              />
              <label htmlFor="geoloc-fotos" className="text-sm text-blue-200 cursor-pointer flex-1">
                📍 Estampar geolocalización (dirección + coordenadas + fecha/hora) en las fotos del montador
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}
              className="bg-transparent border-zinc-700">Cancelar</Button>
            <Button type="submit" disabled={saving || !form.nombre.trim() || !form.cliente_id}
              data-testid="edit-peticion-save"
              className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
              {saving ? "Guardando…" : "Guardar cambios"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
